import React, { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { Pencil, Plus, Trash2 } from 'lucide-react';

import {
	useCreateTenantMutation,
	useDeleteTenantMutation,
	useListTenantsQuery,
	useUpdateTenantMutation,
} from '../api/tenants';
import {
	Alert,
	Button,
	Input,
	Modal,
	ModalBody,
	ModalFooter,
	PageContent,
	PageHeader,
	SearchInput,
	SelectInput,
	SkeletonTable,
	Textarea,
	Typography,
} from '../components/common';
import TenantDomainsModal from '../components/TenantDomainsModal';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../providers/AppAuthProvider';
import { hasRole } from '../utils/utils';

import type { Tenant } from '../api/tenants';
import type { FilterOption } from '../components/common';

const TenantPage: React.FC = () => {
	const [allTenants, setAllTenants] = useState<Tenant[]>([]);
	const [filteredTenants, setFilteredTenants] = useState<Tenant[]>([]);
	const [isSuperAdmin, setIsSuperAdmin] = useState(false);
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [showDomainsModal, setShowDomainsModal] = useState(false);
	const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
	const [tenantToDelete, setTenantToDelete] = useState<Tenant | null>(null);
	const [selectedTenantForDomains, setSelectedTenantForDomains] =
		useState<Tenant | null>(null);

	// Logo upload states
	const [logoFile, setLogoFile] = useState<File | null>(null);
	const [logoPreview, setLogoPreview] = useState<string | null>(null);
	const logoInputRef = React.useRef<HTMLInputElement>(null);

	// Filter and search states
	const [activeOnly, setActiveOnly] = useState<boolean>(false);
	const [searchQuery, setSearchQuery] = useState<string>('');
	const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');

	// Form states
	const [formData, setFormData] = useState<{
		name: string;
		subdomain: string;
		company_email: string;
		company_phone?: string;
		headquarters_address?: string;
		plan?: string;
		status?: string;
		max_enterprises?: number;
		max_users_total?: number;
		max_storage_gb?: number;
		billing_email?: string;
		custom_domain?: string;
		base_domain?: string;
	}>({
		name: '',
		subdomain: '',
		company_email: '',
		company_phone: '',
		headquarters_address: '',
		plan: 'basic',
		status: 'provisioning',
		max_enterprises: 5,
		max_users_total: 50,
		max_storage_gb: 100,
		billing_email: '',
		custom_domain: '',
		base_domain: '',
	});

	const { user } = useAuth();
	const { showToast } = useToast();
	const navigate = useNavigate();

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchQuery(searchQuery);
		}, 300);

		return () => clearTimeout(timer);
	}, [searchQuery]);

	// Check if user is super_admin
	useEffect(() => {
		if (user?.roles) {
			const hasSuperAdmin = hasRole(user.roles, 'super_admin');
			setIsSuperAdmin(hasSuperAdmin);

			if (!hasSuperAdmin) {
				showToast(
					'Access Denied',
					'You do not have permission to access this page',
					'error',
				);
				navigate('/');
			}
		}
	}, [user, navigate, showToast]);

	// API hooks
	const {
		data: tenantsData,
		isLoading,
		isFetching,
		refetch,
	} = useListTenantsQuery({ active_only: activeOnly });

	const [createTenant, { isLoading: creating }] = useCreateTenantMutation();
	const [updateTenant, { isLoading: updating }] = useUpdateTenantMutation();
	const [deleteTenant, { isLoading: deleting }] = useDeleteTenantMutation();

	// Refetch when filter changes
	useEffect(() => {
		refetch();
	}, [activeOnly, refetch]);

	// Update tenants list when data changes
	useEffect(() => {
		if (tenantsData?.tenants) {
			setAllTenants(tenantsData.tenants);
		}
	}, [tenantsData]);

	// Filter tenants based on search and status
	useEffect(() => {
		let filtered = allTenants;

		// Only filter by active status when "Active Only" is selected
		if (activeOnly) {
			filtered = filtered.filter(
				(tenant) =>
					tenant.status === 'active' &&
					(tenant.active === true || tenant.active === undefined),
			);
		}
		// When "All Tenants" is selected, show all tenants (including suspended/deleted)

		if (debouncedSearchQuery.trim()) {
			const query = debouncedSearchQuery.toLowerCase();
			filtered = filtered.filter(
				(tenant) =>
					tenant.name.toLowerCase().includes(query) ||
					tenant.subdomain.toLowerCase().includes(query) ||
					tenant.company_email.toLowerCase().includes(query) ||
					tenant.status.toLowerCase().includes(query),
			);
		}

		setFilteredTenants(filtered);
	}, [allTenants, debouncedSearchQuery, activeOnly]);

	// Filter options
	const statusOptions: FilterOption[] = [
		{ id: 'all', label: 'All Tenants', value: 'all' },
		{ id: 'active', label: 'Active Only', value: 'active' },
	];

	const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			// Validate file type
			if (!file.type.startsWith('image/')) {
				showToast('Invalid File', 'Please select a valid image file', 'error');
				if (logoInputRef.current) {
					logoInputRef.current.value = '';
				}
				return;
			}

			// Validate file size (max 2MB)
			if (file.size > 2 * 1024 * 1024) {
				showToast('File Too Large', 'File size must be less than 2MB', 'error');
				if (logoInputRef.current) {
					logoInputRef.current.value = '';
				}
				return;
			}

			setLogoFile(file);
			const reader = new FileReader();
			reader.onloadend = () => {
				setLogoPreview(reader.result as string);
			};
			reader.readAsDataURL(file);
		}
	};

	const convertToBase64 = (file: File): Promise<string> => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result as string);
			reader.onerror = (error) => reject(error);
		});
	};

	const handleCreateTenant = async () => {
		if (!formData.name || !formData.subdomain || !formData.company_email) {
			showToast(
				'Validation Error',
				'Please fill in all required fields',
				'error',
			);
			return;
		}

		try {
			let logoBase64 = null;
			if (logoFile) {
				logoBase64 = await convertToBase64(logoFile);
			}

			await createTenant({
				...formData,
				logo_base64: logoBase64,
			}).unwrap();
			showToast('Success', 'Tenant created successfully', 'success');
			setShowCreateModal(false);
			resetForm();
			refetch();
		} catch (error: any) {
			console.error('Error creating tenant:', error);
			const errorMessage = error?.data?.message || 'Failed to create tenant';
			showToast('Error', errorMessage, 'error');
		}
	};

	const handleEditTenant = (tenant: Tenant) => {
		setEditingTenant(tenant);
		setFormData({
			name: tenant.name,
			subdomain: tenant.subdomain,
			company_email: tenant.company_email,
			company_phone: tenant.company_phone || '',
			headquarters_address: tenant.headquarters_address || '',
			plan: tenant.plan,
			status: tenant.status,
			max_enterprises: tenant.max_enterprises,
			max_users_total: tenant.max_users_total,
			max_storage_gb: tenant.max_storage_gb,
			billing_email: tenant.billing_email || '',
			custom_domain: (tenant as any).custom_domain || '',
			base_domain: (tenant as any).base_domain || '',
		});
		// Set logo preview if tenant has logo
		if ((tenant as any).logo_base64) {
			setLogoPreview((tenant as any).logo_base64);
		} else {
			setLogoPreview(null);
		}
		setLogoFile(null);
		setShowEditModal(true);
	};

	const handleUpdateTenant = async () => {
		if (!editingTenant) return;

		if (!formData.name || !formData.subdomain || !formData.company_email) {
			showToast(
				'Validation Error',
				'Please fill in all required fields',
				'error',
			);
			return;
		}

		try {
			let logoBase64 = undefined;
			if (logoFile) {
				logoBase64 = await convertToBase64(logoFile);
			} else if (logoPreview && !logoFile) {
				// Keep existing logo if no new file selected
				logoBase64 = logoPreview;
			}

			await updateTenant({
				tenantId: editingTenant.id,
				data: {
					...formData,
					logo_base64: logoBase64,
				},
			}).unwrap();
			showToast('Success', 'Tenant updated successfully', 'success');
			setShowEditModal(false);
			setEditingTenant(null);
			resetForm();
			refetch();
		} catch (error: any) {
			console.error('Error updating tenant:', error);
			const errorMessage = error?.data?.message || 'Failed to update tenant';
			showToast('Error', errorMessage, 'error');
		}
	};

	const handleDeleteClick = (tenant: Tenant) => {
		setTenantToDelete(tenant);
		setShowDeleteModal(true);
	};

	const handleDeleteConfirm = async () => {
		if (!tenantToDelete) return;

		try {
			await deleteTenant(tenantToDelete.id).unwrap();
			showToast('Success', 'Tenant deleted successfully', 'success');
			setShowDeleteModal(false);
			setTenantToDelete(null);
			refetch();
		} catch (error: any) {
			console.error('Error deleting tenant:', error);
			const errorMessage = error?.data?.message || 'Failed to delete tenant';
			showToast('Error', errorMessage, 'error');
		}
	};

	const resetForm = () => {
		setFormData({
			name: '',
			subdomain: '',
			company_email: '',
			company_phone: '',
			headquarters_address: '',
			plan: 'basic',
			status: 'provisioning',
			max_enterprises: 5,
			max_users_total: 50,
			max_storage_gb: 100,
			billing_email: '',
			custom_domain: '',
			base_domain: '',
		});
		setLogoFile(null);
		setLogoPreview(null);
		if (logoInputRef.current) {
			logoInputRef.current.value = '';
		}
	};

	const getStatusBadge = (status?: string | null) => {
		if (!status) {
			return (
				<span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full font-primary bg-neutral-100 text-neutral-600">
					N/A
				</span>
			);
		}

		const statusConfig: Record<
			string,
			{ bg: string; text: string; label: string }
		> = {
			provisioning: {
				bg: 'bg-warning-100',
				text: 'text-warning-800',
				label: 'Provisioning',
			},
			active: {
				bg: 'bg-success-100',
				text: 'text-success-800',
				label: 'Active',
			},
			suspended: {
				bg: 'bg-error-100',
				text: 'text-error-800',
				label: 'Suspended',
			},
			deleted: {
				bg: 'bg-neutral-100',
				text: 'text-neutral-800',
				label: 'Deleted',
			},
		};

		const config = statusConfig[status] || {
			bg: 'bg-neutral-100',
			text: 'text-neutral-600',
			label: status.charAt(0).toUpperCase() + status.slice(1),
		};

		return (
			<span
				className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full font-primary ${config.bg} ${config.text}`}
			>
				{config.label}
			</span>
		);
	};

	const formatDate = (dateString?: string) => {
		if (!dateString) return 'N/A';
		return new Date(dateString).toLocaleDateString();
	};

	if (!isSuperAdmin) {
		return null;
	}

	return (
		<div className="flex flex-1 flex-col">
			<PageHeader
				title="Tenant Management"
				variant="title"
				actions={
					<Button
						onClick={() => {
							resetForm();
							setShowCreateModal(true);
						}}
						variant="primary"
						className="flex items-center gap-2"
						icon={Plus}
					>
						Create Tenant
					</Button>
				}
			/>

			<PageContent className="max-w-[1280px] m-auto">
				{/* Tenants Section */}
				<div className="w-full bg-white border border-gray-200 rounded-lg">
					<div className="flex items-center justify-between mb-6 p-6">
						<Typography
							variant="body1"
							className="text-lg font-normal text-gray-900"
						>
							All Tenants ({filteredTenants.length})
						</Typography>
						<div className="flex items-center gap-4">
							{/* Search */}
							<div className="relative">
								<SearchInput
									placeholder="Search tenants..."
									value={searchQuery}
									onChange={(value) => {
										setSearchQuery(value);
									}}
									className="pl-10 w-64"
								/>
								{isFetching && searchQuery && (
									<div className="absolute inset-y-0 right-3 flex items-center">
										<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
									</div>
								)}
							</div>

							{/* Filter Dropdown - Single Select */}
							<SelectInput
								value={activeOnly ? 'active' : 'all'}
								onChange={(value) => {
									setActiveOnly(value === 'active');
								}}
								options={[
									{ value: 'all', label: 'All Tenants' },
									{ value: 'active', label: 'Active Only' },
								]}
								fullWidth={false}
								className="w-40"
							/>
						</div>
					</div>

					{isLoading || isFetching ? (
						<div className="w-full">
							<SkeletonTable rows={10} columns={8} showHeader={true} />
						</div>
					) : (
						<div className="overflow-x-auto">
							<table className="min-w-full divide-y divide-gray-200">
								<thead className="bg-gray-50">
									<tr>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Name
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Subdomain
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Email
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Plan
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Status
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Created
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Provisioned
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Actions
										</th>
									</tr>
								</thead>
								<tbody className="bg-white divide-y divide-gray-200">
									{filteredTenants.map((tenant) => (
										<tr key={tenant.id}>
											<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
												{tenant.name}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
												{tenant.subdomain}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
												{tenant.company_email}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
												{tenant.plan}
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												{getStatusBadge(tenant.status)}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
												{formatDate(tenant.created_at)}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
												{formatDate(tenant.provisioned_at)}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
												<div className="flex space-x-2">
													<Button
														onClick={() => navigate(`/tenants/${tenant.id}`)}
														variant="outline"
														size="xs"
														className="text-primary-600 hover:text-primary-900"
													>
														View Details
													</Button>
													<Button
														onClick={() => handleEditTenant(tenant)}
														variant="outline"
														size="xs"
														className="text-primary-600 hover:text-primary-900"
														icon={Pencil}
													>
														Edit
													</Button>
													<Button
														onClick={() => handleDeleteClick(tenant)}
														disabled={deleting}
														variant="outline"
														size="xs"
														loading={deleting}
														className="text-error-600 hover:text-error-900"
														icon={Trash2}
													></Button>
												</div>
											</td>
										</tr>
									))}
								</tbody>
							</table>

							{filteredTenants.length === 0 && (
								<div className="text-center py-8">
									<p className="text-gray-500">
										No tenants found matching your criteria.
									</p>
								</div>
							)}
						</div>
					)}
				</div>

				{/* Create Tenant Modal */}
				<Modal
					isOpen={showCreateModal}
					onClose={() => {
						setShowCreateModal(false);
						resetForm();
					}}
					title="Create Tenant"
					size="lg"
				>
					<ModalBody>
						<Typography variant="body2" color="muted" className="mb-4">
							Create a new tenant. All fields marked with * are required.
						</Typography>
						<div className="space-y-4">
							<Input
								label="Company Name *"
								type="text"
								value={formData.name}
								onChange={(e) =>
									setFormData({ ...formData, name: e.target.value })
								}
								placeholder="Enter company name"
							/>
							<Input
								label="Subdomain *"
								type="text"
								value={formData.subdomain}
								onChange={(e) =>
									setFormData({ ...formData, subdomain: e.target.value })
								}
								placeholder="acme"
							/>
							<Input
								label="Company Email *"
								type="email"
								value={formData.company_email}
								onChange={(e) =>
									setFormData({ ...formData, company_email: e.target.value })
								}
								placeholder="admin@company.com"
							/>
							{/* Logo Upload */}
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">
									Company Logo
								</label>
								<div className="flex items-center gap-4">
									{logoPreview ? (
										<div className="flex items-center gap-4">
											<img
												src={logoPreview}
												alt="Logo preview"
												className="h-16 w-16 object-contain border border-gray-300 rounded"
											/>
											<div className="flex flex-col gap-2">
												<Button
													type="button"
													onClick={() => logoInputRef.current?.click()}
													variant="outline"
													size="xs"
												>
													Change Logo
												</Button>
												<Button
													type="button"
													onClick={() => {
														setLogoFile(null);
														setLogoPreview(null);
														if (logoInputRef.current) {
															logoInputRef.current.value = '';
														}
													}}
													variant="outline"
													size="xs"
													className="text-error-600"
												>
													Remove
												</Button>
											</div>
										</div>
									) : (
										<Button
											type="button"
											onClick={() => logoInputRef.current?.click()}
											variant="outline"
											size="sm"
										>
											Upload Logo
										</Button>
									)}
									<input
										ref={logoInputRef}
										type="file"
										accept="image/*"
										onChange={handleLogoSelect}
										className="hidden"
									/>
								</div>
								<p className="mt-1 text-xs text-gray-500">
									PNG, JPG, or SVG. Max 2MB. Recommended: 200x200px
								</p>
							</div>
							<Input
								label="Company Phone"
								type="tel"
								value={formData.company_phone}
								onChange={(e) =>
									setFormData({ ...formData, company_phone: e.target.value })
								}
								placeholder="+1 (555) 123-4567"
							/>
							<Textarea
								label="Headquarters Address"
								value={formData.headquarters_address}
								onChange={(e) =>
									setFormData({
										...formData,
										headquarters_address: e.target.value,
									})
								}
								placeholder="Enter full address"
								rows={3}
							/>
							<div className="grid grid-cols-2 gap-4">
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Plan
									</label>
									<select
										value={formData.plan}
										onChange={(e) =>
											setFormData({ ...formData, plan: e.target.value })
										}
										className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500"
									>
										<option value="basic">Basic</option>
										<option value="professional">Professional</option>
										<option value="enterprise">Enterprise</option>
									</select>
								</div>
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Status
									</label>
									<select
										value={formData.status}
										onChange={(e) =>
											setFormData({ ...formData, status: e.target.value })
										}
										className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500"
									>
										<option value="provisioning">Provisioning</option>
										<option value="active">Active</option>
										<option value="suspended">Suspended</option>
									</select>
								</div>
							</div>
							<div className="grid grid-cols-3 gap-4">
								<Input
									label="Max Organizations"
									type="number"
									value={formData.max_enterprises?.toString()}
									onChange={(e) =>
										setFormData({
											...formData,
											max_enterprises: parseInt(e.target.value) || 0,
										})
									}
									placeholder="5"
								/>
								<Input
									label="Max Users Total"
									type="number"
									value={formData.max_users_total?.toString()}
									onChange={(e) =>
										setFormData({
											...formData,
											max_users_total: parseInt(e.target.value) || 0,
										})
									}
									placeholder="50"
								/>
								<Input
									label="Max Storage (GB)"
									type="number"
									value={formData.max_storage_gb?.toString()}
									onChange={(e) =>
										setFormData({
											...formData,
											max_storage_gb: parseInt(e.target.value) || 0,
										})
									}
									placeholder="100"
								/>
							</div>
							<Input
								label="Billing Email"
								type="email"
								value={formData.billing_email}
								onChange={(e) =>
									setFormData({ ...formData, billing_email: e.target.value })
								}
								placeholder="billing@company.com"
							/>
							<div className="grid grid-cols-2 gap-4">
								<Input
									label="Custom Domain"
									type="text"
									value={formData.custom_domain || ''}
									onChange={(e) =>
										setFormData({ ...formData, custom_domain: e.target.value })
									}
									placeholder="app.coastalpay.com"
									helperText="Optional: Custom domain (e.g., app.coastalpay.com)"
								/>
								<Input
									label="Base Domain"
									type="text"
									value={formData.base_domain || ''}
									onChange={(e) =>
										setFormData({ ...formData, base_domain: e.target.value })
									}
									placeholder="rocketfee.com"
									helperText="Optional: Base domain for subdomain (auto-detected if not provided)"
								/>
							</div>
						</div>
					</ModalBody>
					<ModalFooter>
						<Button
							onClick={() => {
								setShowCreateModal(false);
								resetForm();
							}}
							variant="secondary"
						>
							Cancel
						</Button>
						<Button
							onClick={handleCreateTenant}
							disabled={creating}
							variant="primary"
							loading={creating}
						>
							Create Tenant
						</Button>
					</ModalFooter>
				</Modal>

				{/* Edit Tenant Modal */}
				<Modal
					isOpen={showEditModal && !!editingTenant}
					onClose={() => {
						setShowEditModal(false);
						setEditingTenant(null);
						resetForm();
					}}
					title="Edit Tenant"
					size="lg"
				>
					<ModalBody>
						<Typography variant="body2" color="muted" className="mb-4">
							Update tenant information.
						</Typography>
						<div className="space-y-4">
							<Input
								label="Company Name *"
								type="text"
								value={formData.name}
								onChange={(e) =>
									setFormData({ ...formData, name: e.target.value })
								}
								placeholder="Enter company name"
							/>
							<Input
								label="Subdomain *"
								type="text"
								value={formData.subdomain}
								onChange={(e) =>
									setFormData({ ...formData, subdomain: e.target.value })
								}
								placeholder="acme"
							/>
							<Input
								label="Company Email *"
								type="email"
								value={formData.company_email}
								onChange={(e) =>
									setFormData({ ...formData, company_email: e.target.value })
								}
								placeholder="admin@company.com"
							/>
							{/* Logo Upload */}
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">
									Company Logo
								</label>
								<div className="flex items-center gap-4">
									{logoPreview ? (
										<div className="flex items-center gap-4">
											<img
												src={logoPreview}
												alt="Logo preview"
												className="h-16 w-16 object-contain border border-gray-300 rounded"
											/>
											<div className="flex flex-col gap-2">
												<Button
													type="button"
													onClick={() => logoInputRef.current?.click()}
													variant="outline"
													size="xs"
												>
													Change Logo
												</Button>
												<Button
													type="button"
													onClick={() => {
														setLogoFile(null);
														setLogoPreview(null);
														if (logoInputRef.current) {
															logoInputRef.current.value = '';
														}
													}}
													variant="outline"
													size="xs"
													className="text-error-600"
												>
													Remove
												</Button>
											</div>
										</div>
									) : (
										<Button
											type="button"
											onClick={() => logoInputRef.current?.click()}
											variant="outline"
											size="sm"
										>
											Upload Logo
										</Button>
									)}
									<input
										ref={logoInputRef}
										type="file"
										accept="image/*"
										onChange={handleLogoSelect}
										className="hidden"
									/>
								</div>
								<p className="mt-1 text-xs text-gray-500">
									PNG, JPG, or SVG. Max 2MB. Recommended: 200x200px
								</p>
							</div>
							<Input
								label="Company Phone"
								type="tel"
								value={formData.company_phone}
								onChange={(e) =>
									setFormData({ ...formData, company_phone: e.target.value })
								}
								placeholder="+1 (555) 123-4567"
							/>
							<Textarea
								label="Headquarters Address"
								value={formData.headquarters_address}
								onChange={(e) =>
									setFormData({
										...formData,
										headquarters_address: e.target.value,
									})
								}
								placeholder="Enter full address"
								rows={3}
							/>
							<div className="grid grid-cols-2 gap-4">
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Plan
									</label>
									<select
										value={formData.plan}
										onChange={(e) =>
											setFormData({ ...formData, plan: e.target.value })
										}
										className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500"
									>
										<option value="basic">Basic</option>
										<option value="professional">Professional</option>
										<option value="enterprise">Enterprise</option>
									</select>
								</div>
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Status
									</label>
									<select
										value={formData.status}
										onChange={(e) =>
											setFormData({ ...formData, status: e.target.value })
										}
										className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500"
									>
										<option value="provisioning">Provisioning</option>
										<option value="active">Active</option>
										<option value="suspended">Suspended</option>
										<option value="deleted">Deleted</option>
									</select>
								</div>
							</div>
							<div className="grid grid-cols-3 gap-4">
								<Input
									label="Max Organizations"
									type="number"
									value={formData.max_enterprises?.toString()}
									onChange={(e) =>
										setFormData({
											...formData,
											max_enterprises: parseInt(e.target.value) || 0,
										})
									}
									placeholder="5"
								/>
								<Input
									label="Max Users Total"
									type="number"
									value={formData.max_users_total?.toString()}
									onChange={(e) =>
										setFormData({
											...formData,
											max_users_total: parseInt(e.target.value) || 0,
										})
									}
									placeholder="50"
								/>
								<Input
									label="Max Storage (GB)"
									type="number"
									value={formData.max_storage_gb?.toString()}
									onChange={(e) =>
										setFormData({
											...formData,
											max_storage_gb: parseInt(e.target.value) || 0,
										})
									}
									placeholder="100"
								/>
							</div>
							<Input
								label="Billing Email"
								type="email"
								value={formData.billing_email}
								onChange={(e) =>
									setFormData({ ...formData, billing_email: e.target.value })
								}
								placeholder="billing@company.com"
							/>
							<div className="grid grid-cols-2 gap-4">
								<Input
									label="Custom Domain"
									type="text"
									value={formData.custom_domain || ''}
									onChange={(e) =>
										setFormData({ ...formData, custom_domain: e.target.value })
									}
									placeholder="app.coastalpay.com"
									helperText="Optional: Custom domain (e.g., app.coastalpay.com)"
								/>
								<Input
									label="Base Domain"
									type="text"
									value={formData.base_domain || ''}
									onChange={(e) =>
										setFormData({ ...formData, base_domain: e.target.value })
									}
									placeholder="rocketfee.com"
									helperText="Optional: Base domain for subdomain (auto-detected if not provided)"
								/>
							</div>
						</div>
					</ModalBody>
					<ModalFooter>
						<Button
							onClick={() => {
								setShowEditModal(false);
								setEditingTenant(null);
								resetForm();
							}}
							variant="secondary"
						>
							Cancel
						</Button>
						<Button
							onClick={handleUpdateTenant}
							disabled={updating}
							variant="primary"
							loading={updating}
						>
							Update Tenant
						</Button>
					</ModalFooter>
				</Modal>

				{/* Delete Confirmation Modal */}
				<Modal
					isOpen={showDeleteModal}
					onClose={() => {
						setShowDeleteModal(false);
						setTenantToDelete(null);
					}}
					title="Delete Tenant"
					size="md"
				>
					<ModalBody>
						<Alert variant="warning" className="mb-4">
							This action cannot be undone. Deleting a tenant will permanently
							remove all associated data.
						</Alert>
						<Typography variant="body2" color="muted">
							Are you sure you want to delete{' '}
							<strong>{tenantToDelete?.name}</strong>? This will delete the
							tenant and all associated data.
						</Typography>
					</ModalBody>
					<ModalFooter>
						<Button
							onClick={() => {
								setShowDeleteModal(false);
								setTenantToDelete(null);
							}}
							variant="secondary"
						>
							Cancel
						</Button>
						<Button
							onClick={handleDeleteConfirm}
							disabled={deleting}
							variant="primary"
							loading={deleting}
							className="bg-error-600 hover:bg-error-700"
						>
							Delete Tenant
						</Button>
					</ModalFooter>
				</Modal>

				{/* Domains Modal */}
				{selectedTenantForDomains && (
					<TenantDomainsModal
						isOpen={showDomainsModal}
						onClose={() => {
							setShowDomainsModal(false);
							setSelectedTenantForDomains(null);
						}}
						tenantId={selectedTenantForDomains.id}
						tenantName={selectedTenantForDomains.name}
					/>
				)}
			</PageContent>
		</div>
	);
};

export default TenantPage;
